import { RwBadge } from '@components/atoms';
import { RwLoading } from '@components/RwLoading';
import { RwEmpty } from '@components/RwEmpty';
import { t } from '@services/i18n';
import { useApiQuery } from '@hooks/useApiQuery';
import type { Badge } from '@shared/types';
import type { MemberScreenProps } from './types';

interface BadgesResponse {
  badges: Badge[];
}

/**
 * Achievements — earned badges (rarity tag, earned date, +REP) and in-progress
 * badges (locked badge glyph with a progress bar). Renders only real badges
 * from the site `badges` endpoint; shows a calm empty state when there are none.
 */
export function UserBadges(_props: MemberScreenProps) {
  const { data, isLoading } = useApiQuery<BadgesResponse>(['rw-badges'], 'badges', { fallback: { badges: [] } });
  if (isLoading) return <RwLoading variant="cards" />;
  const badges = data?.badges ?? [];
  const earned = badges.filter((b) => b.earned);
  const locked = badges.filter((b) => !b.earned);

  return (
    <div className="rw-page rw-user">
      <header className="rw-page-h">
        <div>
          <div className="rw-eyebrow">{t('COM_REWARDIFY_UBADGE_ACHIEVEMENTS', 'Achievements')}</div>
          <h1 className="rw-h1">{t('COM_REWARDIFY_UBADGE_MY_BADGES', 'My badges')}</h1>
          <p className="rw-lede">
            {t(
              'COM_REWARDIFY_UBADGE_LEDE',
              '%d earned · %d to go. Earned badges can be exported as verifiable Open Badges.',
              earned.length,
              locked.length,
            )}
          </p>
        </div>
      </header>

      <h3 className="rw-section-t">{t('COM_REWARDIFY_UBADGE_EARNED', 'Earned')}</h3>
      {earned.length === 0 ? (
        <RwEmpty
          title={t('COM_REWARDIFY_UBADGE_EMPTY_EARNED_TITLE', 'No badges earned yet')}
          hint={t('COM_REWARDIFY_UBADGE_EMPTY_EARNED_HINT', 'Earn badges by staying active. They will appear here.')}
        />
      ) : (
        <div className="rw-badge-grid">
          {earned.map((b) => (
            <div key={b.id} className="rw-badge-card">
              <div className="rw-badge-card-top">
                <RwBadge tier={b.tier} icon={b.icon} size={72} />
                <span className={`rw-tag rw-tag-${b.rarity}`}>{b.rarity}</span>
              </div>
              <h4 className="rw-badge-name">{b.name}</h4>
              <p className="rw-badge-desc">{b.desc}</p>
              <div className="rw-fg2" style={{ fontSize: 11, marginTop: 8 }}>
                {t('COM_REWARDIFY_UBADGE_EARNED_ON', 'Earned %s · +%d REP', b.earnedOn ?? '', b.points)}
              </div>
              {b.credentialUrl && (
                <a
                  className="rw-btn rw-btn-ghost rw-btn-sm"
                  style={{ marginTop: 10, display: 'inline-flex' }}
                  href={b.credentialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('COM_REWARDIFY_UBADGE_VERIFIABLE_CREDENTIAL', 'Open Badge credential')}
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      <h3 className="rw-section-t" style={{ marginTop: 32 }}>
        {t('COM_REWARDIFY_UBADGE_IN_PROGRESS', 'In progress')}
      </h3>
      {locked.length === 0 ? (
        <RwEmpty
          title={t('COM_REWARDIFY_UBADGE_EMPTY_PROGRESS_TITLE', 'No badges in progress')}
          hint={t('COM_REWARDIFY_UBADGE_EMPTY_PROGRESS_HINT', 'Badges you are working toward will show their progress here.')}
        />
      ) : (
        <div className="rw-badge-grid">
          {locked.map((b) => (
            <div key={b.id} className="rw-badge-card rw-locked">
              <div className="rw-badge-card-top">
                <RwBadge tier={b.tier} icon={b.icon} size={72} locked progress={b.progress} />
                <span className={`rw-tag rw-tag-${b.rarity}`}>{b.rarity}</span>
              </div>
              <h4 className="rw-badge-name">{b.name}</h4>
              <p className="rw-badge-desc">{b.desc}</p>
              <div className="rw-progress-bar" style={{ marginTop: 10 }}>
                <div style={{ width: `${(b.progress ?? 0) * 100}%` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11 }}>
                <span className="rw-fg2 rw-mono">{Math.round((b.progress ?? 0) * 100)}%</span>
                <span className="rw-fg2 rw-mono">{t('COM_REWARDIFY_UBADGE_PLUS_REP', '+%d REP', b.points)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
